import React from 'react'

import {
  ExpandedState,
  createColumnHelper,
  getCoreRowModel,
  getExpandedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useTranslation } from 'react-i18next'

import {
  Box,
  DataTable,
  Flex,
  IconButton,
  Stack,
  Text,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronDownRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'
import { getTextAlignLightTableStyle } from '@enechain/eposition-ui'

import {
  FeeLegResultModel,
  FeeResultModel,
} from '~/pages/Simulation/models/SimulationResultModel/FeeResultModel'

type Props = {
  feeResults: FeeResultModel[] | undefined
}

// 注文の行(閉じた行)と、スプレッドの注文を開いたときの脚ごとの行
type FeeTableRow = FeeResultModel | FeeLegResultModel

function isLegRow(row: FeeTableRow): row is FeeLegResultModel {
  return 'legType' in row
}

// 開閉できる行の注文番号と先頭を揃えるため、トグルと同じ幅を空けて中身を出す
const TogglePlaceholder: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Flex alignItems="center" gap="1">
    <IconButton
      aria-hidden
      aria-label=""
      backgroundColor="transparent"
      size="xs"
      tabIndex={-1}
      visibility="hidden"
    >
      <FaChevronRightRegular />
    </IconButton>
    <Box>{children}</Box>
  </Flex>
)

const resultFeeTable: React.FC<Props> = ({ feeResults }) => {
  const { t } = useTranslation(['page', 'common', 'domain'])
  const [expanded, setExpanded] = React.useState<ExpandedState>({})
  const columnHelper = createColumnHelper<FeeTableRow>()
  const hasExpandableRow = (feeResults ?? []).some(
    (result) => result.legFees !== undefined && result.legFees.length > 0,
  )

  const columns = [
    columnHelper.display({
      id: 'orderNumber',
      header: () => {
        if (!hasExpandableRow) {
          return t('page:simulation.order_relation_number')
        }
        return (
          <TogglePlaceholder>
            {t('page:simulation.order_relation_number')}
          </TogglePlaceholder>
        )
      },
      cell: ({ row }) => {
        const original = row.original
        if (isLegRow(original)) {
          return null
        }
        if (!row.getCanExpand()) {
          if (!hasExpandableRow) {
            return <Box>{original.orderNumber}</Box>
          }
          return <TogglePlaceholder>{original.orderNumber}</TogglePlaceholder>
        }
        return (
          <Flex alignItems="center" gap="1">
            <IconButton
              aria-label={
                row.getIsExpanded()
                  ? t('common:action.negative.close')
                  : t('common:action.positive.open')
              }
              aria-expanded={row.getIsExpanded()}
              backgroundColor="transparent"
              size="xs"
              onClick={row.getToggleExpandedHandler()}
            >
              {row.getIsExpanded() ? (
                <FaChevronDownRegular />
              ) : (
                <FaChevronRightRegular />
              )}
            </IconButton>
            <Box>{original.orderNumber}</Box>
          </Flex>
        )
      },
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'orderSummary',
      header: t('page:simulation.order_summary_text'),
      cell: ({ row }) => {
        const original = row.original
        if (isLegRow(original)) {
          return (
            <Text paddingLeft="4" color="Text.midEmphasis">
              {original.orderSummary}
            </Text>
          )
        }
        return <Text>{original.orderSummary}</Text>
      },
      enableSorting: false,
    }),
    columnHelper.display({
      id: 'fee',
      header: () => (
        <Text css={{ textTransform: 'none' }}>{t('page:simulation.fee')}</Text>
      ),
      cell: ({ row }) => {
        const original = row.original
        if (isLegRow(original)) {
          return <Text color="Text.midEmphasis">{original.fee}</Text>
        }
        if (original.isFeeHidden) {
          return <Text>{t('page:simulation.fee_hidden')}</Text>
        }
        // 合算はバックエンドが出した値をそのまま出す
        return <Text>{original.fee}</Text>
      },
      enableSorting: false,
      meta: getTextAlignLightTableStyle(),
    }),
  ]

  const table = useReactTable<FeeTableRow>({
    columns,
    data: feeResults ?? [],
    state: {
      expanded,
    },
    onExpandedChange: setExpanded,
    getSubRows: (row) => (isLegRow(row) ? undefined : row.legFees),
    getRowCanExpand: (row) =>
      !isLegRow(row.original) &&
      row.original.legFees !== undefined &&
      row.original.legFees.length > 0,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
  })

  if (feeResults === undefined) {
    return null
  }

  return (
    <Stack>
      <DataTable
        table={table}
        emptyComponent={
          <Box width="100%" textAlign="center">
            {t('common:error.empty_data')}
          </Box>
        }
      />
    </Stack>
  )
}

export const ResultFeeTable = React.memo(resultFeeTable)
